import { useToolset } from "@/hooks/toolTypes";
import { useMissingRequiredEnvVars } from "@/hooks/useMissingEnvironmentVariables";
import type { Toolset } from "@/lib/toolTypes";
import { useRoutes } from "@/routes";
import { useGetMcpMetadata } from "@gram/client/react-query/getMcpMetadata.js";
import { useListEnvironments } from "@gram/client/react-query/listEnvironments.js";
import { AlertCircle, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Text } from "@/components/ui/Text";
import { usePlaygroundIssuerConnection } from "./usePlaygroundIssuerConnection";
import { PlaygroundChat } from "./PlaygroundChat";

interface PlaygroundElementsProps {
  toolsetSlug: string | null;
  environmentSlug: string | null;
  model: string;
  /** Additional action buttons to render alongside the share button */
  additionalActions?: React.ReactNode;
  /** Slug of the playground environment for user-provided variables */
  playgroundEnvironmentSlug?: string;
}

/**
 * The toolset-backed playground variant: resolves a toolset to its Speakeasy-hosted
 * `/mcp/<slug>` URL, mints an issuer-gated gateway token, surfaces
 * missing-auth / login notices, then renders the shared {@link PlaygroundChat}.
 */
export function PlaygroundElements({
  toolsetSlug,
  environmentSlug,
  model,
  additionalActions,
  playgroundEnvironmentSlug,
}: PlaygroundElementsProps): JSX.Element {
  // Get toolset data to construct MCP URL
  const {
    data: toolset,
    isLoading: isToolsetLoading,
    isError: isToolsetError,
    isFetching: isToolsetFetching,
    refetch: refetchToolset,
  } = useToolset(toolsetSlug ?? undefined, { throwOnError: false });

  // Get environments and MCP metadata for auth status check
  const { data: environmentsData } = useListEnvironments();
  const environments = environmentsData?.environments ?? [];
  const { data: mcpMetadataData } = useGetMcpMetadata(
    { toolsetSlug: toolsetSlug ?? "" },
    undefined,
    { throwOnError: false, retry: false, enabled: !!toolsetSlug },
  );
  const mcpMetadata = mcpMetadataData?.metadata;
  const defaultEnvironmentSlug =
    environments.find((env) => env.id === mcpMetadata?.defaultEnvironmentId)
      ?.slug ?? "default";

  // ToolsetEntry from useListToolsets is structurally compatible with Toolset
  // for the fields useMissingRequiredEnvVars accesses (same pattern as Playground.tsx)
  //
  // Intentionally do NOT pass playgroundEnvironmentSlug here. The playground
  // environment only stores user-provided entries, so system variables would
  // always appear missing if we pointed the hook at it. User-provided vars
  // are already treated as always-configured by useMissingRequiredEnvVars
  // regardless of the environment, so using the default env here is correct
  // for both kinds of variables.
  const missingAuthCount = useMissingRequiredEnvVars(
    toolset as Toolset | undefined,
    environments,
    environmentSlug ?? defaultEnvironmentSlug,
    mcpMetadata,
  );

  // Issuer-gated toolsets mint a user-session JWT and link upstream sessions via
  // the first-party connect flow. The shared hook mints the token (sent as the
  // chat's `Authorization: Bearer`) and probes the endpoint so we can block the
  // chat until the dashboard user has connected. React Query dedupes the mint
  // and probe with the auth panel (PlaygroundAuth), so this costs no extra calls.
  const issuerConnection = usePlaygroundIssuerConnection(
    toolset as Toolset | undefined,
  );

  // The minted user-session JWT is what the elements MCP client sends as
  // `Authorization: Bearer` on /mcp/{slug} requests, so the runtime gateway
  // resolves the dashboard user's stored upstream credentials via the same path
  // a real MCP client would after an OAuth dance — no special-casing in
  // ApplyIssuerGate.
  const gatewayToken = issuerConnection.accessToken;
  const mcpUrl = issuerConnection.mcpUrl;

  if (!toolsetSlug) {
    return (
      <ConnectionNotice>
        Select an MCP server to start chatting
      </ConnectionNotice>
    );
  }

  if (
    isToolsetLoading ||
    (isToolsetError && isToolsetFetching) ||
    issuerConnection.isLoading
  ) {
    return (
      <ConnectionNotice>
        Connecting to the selected MCP server…
      </ConnectionNotice>
    );
  }

  if (isToolsetError || issuerConnection.isError) {
    return (
      <ConnectionNotice
        error
        onRetry={
          isToolsetError
            ? () => void refetchToolset({ cancelRefetch: false })
            : issuerConnection.refetch
        }
      >
        {isToolsetError
          ? "Unable to load the selected MCP server. Try again."
          : issuerConnection.errorMessage}
      </ConnectionNotice>
    );
  }

  if (!mcpUrl || !toolset) {
    return (
      <ConnectionNotice
        error
        onRetry={
          !toolset
            ? () => void refetchToolset({ cancelRefetch: false })
            : issuerConnection.refetch
        }
      >
        The selected MCP server is unavailable.
      </ConnectionNotice>
    );
  }

  // Block the chat when an issuer-gated toolset needs the dashboard user to link
  // their upstream session: the probe 401'd (`needsAuth`), so tool calls would
  // fail until they Connect in the auth panel. The connect button lives in
  // PlaygroundAuth (the sidebar), which shares this hook's probe state.
  if (issuerConnection.isIssuerGated && issuerConnection.needsAuth) {
    return <LoginRequiredNotice providerName={toolset?.name ?? "provider"} />;
  }

  // A cached token alone is not proof that the upstream session is linked.
  if (
    issuerConnection.isIssuerGated &&
    (!gatewayToken || !issuerConnection.connected)
  ) {
    return (
      <ConnectionNotice>
        Connecting to the selected MCP server…
      </ConnectionNotice>
    );
  }

  return (
    <PlaygroundChat
      mcpUrl={mcpUrl}
      gatewayToken={gatewayToken}
      model={model}
      environmentSlug={environmentSlug}
      playgroundEnvironmentSlug={playgroundEnvironmentSlug}
      toolset={toolset}
      additionalActions={additionalActions}
      banner={
        missingAuthCount > 0 ? (
          <AuthWarningBanner
            missingCount={missingAuthCount}
            toolsetSlug={toolsetSlug}
          />
        ) : undefined
      }
    />
  );
}

function ConnectionNotice({
  children,
  error = false,
  onRetry,
}: {
  children: React.ReactNode;
  error?: boolean;
  onRetry?: () => void;
}) {
  return (
    <div
      className="flex h-full flex-col gap-3 items-center justify-center"
      role={error ? "alert" : "status"}
    >
      <Text muted>{children}</Text>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

function AuthWarningBanner({
  missingCount,
  toolsetSlug,
}: {
  missingCount: number;
  toolsetSlug: string;
}) {
  const routes = useRoutes();

  return (
    <div className="bg-warning/15 border-warning/30 text-warning-foreground flex items-center gap-2 border-b px-4 py-2.5 text-sm font-medium">
      <AlertCircle className="size-4 shrink-0" />
      <span>
        {missingCount} authentication{" "}
        {missingCount === 1 ? "variable" : "variables"} not configured.{" "}
        <routes.mcp.details.Link
          params={[toolsetSlug]}
          hash="authentication"
          className="hover:text-foreground font-medium underline"
        >
          Configure now
        </routes.mcp.details.Link>
      </span>
    </div>
  );
}

function LoginRequiredNotice({ providerName }: { providerName: string }) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="flex max-w-md flex-col items-center gap-3 px-4 text-center">
        <div className="bg-warning/15 rounded-full p-3">
          <ShieldAlert className="text-warning size-6" />
        </div>
        <Text className="font-medium">Login Required</Text>
        <Text muted className="text-sm">
          This MCP server requires you to connect your{" "}
          <span className="text-foreground font-medium">{providerName}</span>{" "}
          account. Use the{" "}
          <span className="text-foreground font-medium">Connect</span> button in
          the Authentication section of the sidebar to sign in.
        </Text>
      </div>
    </div>
  );
}
